// The web app is a client of the public /v1 API, typed from contracts/openapi/v1.json.
import createClient from 'openapi-fetch';
import type { paths } from './api-schema';

export type {
  SchemaAdminOrganization as AdminOrganization,
  SchemaAlarm as Alarm,
  SchemaAlarmEvent as AlarmEvent,
  SchemaAlarmStat as AlarmStat,
  SchemaApiKey as ApiKey,
  SchemaApiKeys as ApiKeys,
  SchemaDatapoint as Datapoint,
  SchemaDevice as Device,
  SchemaEnrollment as Enrollment,
  SchemaHistory as History,
  SchemaInvitation as Invitation,
  SchemaLatestValue as LatestValue,
  SchemaMe as Me,
  SchemaMember as Member,
  SchemaMembers as Members,
  SchemaNewApiKey as NewApiKey,
  SchemaPlant as Plant,
  SchemaSeries as Series,
  SchemaSyncStatus as SyncStatus,
} from './api-schema';

export const api = createClient<paths>({ baseUrl: '/v1', credentials: 'same-origin' });

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly problem: { title?: string; detail?: string; issues?: { path: string; message: string }[] },
  ) {
    super(problem.detail ?? problem.title ?? `Request failed (${status})`);
  }
}

/** Data, or an ApiError with the server's problem details. */
export async function unwrap<T>(request: Promise<{ data?: T; error?: unknown; response: Response }>): Promise<T> {
  const { data, error, response } = await request;
  if (!response.ok) throw new ApiError(response.status, (error ?? {}) as ApiError['problem']);
  return data as T;
}

/** Better Auth endpoints (not part of the /v1 API). */
export async function authRequest<T = unknown>(path: string, body?: unknown): Promise<T> {
  const res = await fetch(`/auth${path}`, {
    method: body === undefined ? 'GET' : 'POST',
    credentials: 'same-origin',
    headers: body === undefined ? {} : { 'Content-Type': 'application/json' },
    body: body === undefined ? undefined : JSON.stringify(body),
  });
  const text = await res.text();
  const json = text ? JSON.parse(text) : null;
  if (!res.ok) throw new ApiError(res.status, { title: json?.code ?? res.statusText, detail: json?.message ?? json?.detail });
  return json as T;
}

export async function signInWith(provider: 'microsoft' | 'google', next: string): Promise<void> {
  const origin = window.location.origin;
  const res = await authRequest<{ url?: string }>('/sign-in/social', {
    provider,
    callbackURL: `${origin}${next}`,
    errorCallbackURL: `${origin}/sign-in?error=1`,
  });
  if (res.url) window.location.href = res.url;
}

export async function signOut(): Promise<void> {
  await authRequest('/sign-out', {});
}
